import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import WebLayout from "../components/WebLayout";
import Seo from "../components/Seo";
import TurnstileWidget from "../components/TurnstileWidget";
import { toast } from "sonner";
import {
  CheckCircle2, AlertCircle, Loader2, ShieldCheck, Handshake,
  Building2, MapPin, ClipboardList, ChevronDown, Plus, Minus,
} from "lucide-react";

// ─── Constants ─────────────────────────────────────────────────────────
const API = process.env.REACT_APP_BACKEND_URL || "";
const CONSENT_VERSION = "tos-2026-08-12_privacy-2026-08-12"; // matches Terms.jsx + Privacy.jsx LAST_UPDATED
const IMG_HERO = "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/e95cd7818c071daeee3ec58b1ecc7eca8364006198a0e5d998c6af0c10be0d25.jpeg";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM",
  "NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA",
  "WV","WI","WY",
];

const BUSINESS_TYPES = [
  "Warehouse/Distribution",
  "Manufacturing",
  "Office/Workplace",
  "Apartment/Residential Community",
  "Truck/Auto Facility",
  "Gym/Fitness",
  "Senior Living",
  "Dealership",
  "Other",
];

const VENDING_TYPES = [
  { value: "Snack",                   label: "Snack" },
  { value: "Beverage/Cold Drinks",    label: "Beverage / Cold Drinks" },
  { value: "Combo",                   label: "Combo" },
  { value: "Coffee & Beverage",       label: "Coffee & Beverage" },
  { value: "Fresh Food/Smart Cooler", label: "Fresh Food / Smart Cooler" },
  { value: "Micro Market",            label: "Micro Market" },
  { value: "Not Sure",                label: "Not sure yet" },
];

const PEOPLE_BUCKETS = [
  { value: "<50",       label: "Fewer than 50" },
  { value: "50-150",    label: "50 – 150" },
  { value: "150-300",   label: "150 – 300" },
  { value: "300-750",   label: "300 – 750" },
  { value: "750-2000",  label: "750 – 2,000" },
  { value: "2000+",     label: "2,000+" },
];

const SCHEDULES = [
  { value: "24_7",            label: "24/7" },
  { value: "business_hours",  label: "Business hours only" },
  { value: "multi_shift",     label: "Multi-shift" },
];

const DM_ROLES = [
  { value: "owner_manager", label: "I'm the owner/manager/decision-maker for this location." },
  { value: "helps_decide",  label: "I help make these decisions." },
  { value: "can_connect",   label: "I can connect SpotBounty with the decision-maker." },
  { value: "not_involved",  label: "I'm not involved in the decision." },
];

const MACHINES_DESIRED = [
  { value: "1",        label: "1" },
  { value: "2",        label: "2" },
  { value: "3+",       label: "3+" },
  { value: "not_sure", label: "Not sure" },
];

const COMMISSION_EXPECTATIONS = [
  { value: "standard", label: "Standard operator terms" },
  { value: "specific", label: "We have specific expectations" },
  { value: "not_sure", label: "Not sure yet" },
];

const FAQ_ITEMS = [
  { q: "Does requesting vending cost my business anything?",
    a: "No. Requesting vending through SpotBounty is free for your business. Independent vending operators fund and service their own machines. Commission or revenue share, if any, is agreed directly between your business and the selected operator and varies depending on the location and format." },
  { q: "Who owns and services the machines?",
    a: "The independent vending operator we introduce you to owns the machines and handles installation, restocking, maintenance, and repairs directly. SpotBounty is the marketplace connecting your business to that operator — SpotBounty itself does not own or service vending equipment." },
  { q: "What types of vending can I request?",
    a: "Snack, beverage, combo, coffee, fresh food or smart cooler, and micro market formats are commonly requested. Availability of each format depends on the selected operator and location." },
  { q: "What makes a location a good fit?",
    a: "Foot traffic, number of employees or residents, hours and shifts, existing vending, and access for restocking all factor in. Larger populations and multi-shift operations tend to be the most operator-attractive, but there are exceptions — send us the details and we'll take a look." },
  { q: "Can I request vending for multiple locations?",
    a: "Yes. Indicate on the form that you have multiple locations and share an approximate site count. For larger portfolios, mention any procurement or reporting requirements in the notes — we'll route to operators equipped for multi-site coordination." },
  { q: "What if I have procurement or insurance requirements?",
    a: "Share those in the notes. When matching a location, SpotBounty can take requirements such as insurance / COI availability, W-9 documentation, vendor agreements, service expectations, and reporting needs into account. Documentation, contracting, and ongoing vending service are provided directly by the selected independent operator." },
  { q: "Can my business request a commission or revenue share?",
    a: "You can indicate your expectations on the form. Commission, revenue share, or rent terms — if any — are negotiated directly between your business and the operator, and depend on the operator, location, format, and volume." },
  { q: "What happens after I submit?",
    a: "SpotBounty reviews your location and may contact you if we need additional information. If the location looks like a fit, we work on connecting you with an independent vending operator, who then handles machine and service details with you directly." },
];

// ─── Helpers ───────────────────────────────────────────────────────────
function readCookie(name) {
  try {
    return decodeURIComponent(
      document.cookie.split("; ").find((c) => c.startsWith(name + "="))?.split("=")[1] || ""
    );
  } catch { return ""; }
}

function formatPhoneUS(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
}

// ─── Main Page ─────────────────────────────────────────────────────────
export default function BusinessesRequest() {
  const [params, setParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(params.get("submitted") === "true");
  const [confirmation, setConfirmation] = useState(null);

  // Restore confirmation from sessionStorage on refresh so ?submitted=true is not empty
  useEffect(() => {
    if (params.get("submitted") === "true") {
      try {
        const saved = sessionStorage.getItem("sb_biz_confirmation");
        if (saved) setConfirmation(JSON.parse(saved));
      } catch { /* ignore */ }
    }
  }, [params]);

  return (
    <WebLayout>
      <Seo
        title="Request Vending for Your Business"
        description="Request vending service for your workplace. SpotBounty is a marketplace connecting your business with independent vending operators."
        path="/businesses"
      />
      <Hero />
      <TrustStrip />
      <HowItWorks />
      {!submitted ? (
        <RequestForm
          onSuccess={(payload) => {
            setConfirmation(payload);
            try { sessionStorage.setItem("sb_biz_confirmation", JSON.stringify(payload)); } catch { /* ignore */ }
            setSubmitted(true);
            setParams({ submitted: "true" }, { replace: true });
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      ) : (
        <ConfirmationState data={confirmation} />
      )}
      <ProcurementSection />
      <FaqAccordion />
    </WebLayout>
  );
}

// ─── Section: Hero ─────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="relative border-b border-[#2A2F34] overflow-hidden">
      <div className="absolute inset-0" aria-hidden>
        <img
          src={IMG_HERO}
          alt=""
          width={2000}
          height={1200}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#080B0D] via-[#080B0D]/75 to-[#080B0D]/25" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#080B0D]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 pt-16 sm:pt-24 pb-16 sm:pb-24">
        <div className="max-w-2xl">
          <div className="sb-eyebrow flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#FFC107]" /> For Businesses
          </div>
          <h1 className="mt-4 text-[40px] sm:text-[56px] lg:text-[64px] font-black leading-[1.03] tracking-tight">
            NEED VENDING AT<br /><span className="text-[#FFC107]">YOUR WORKPLACE?</span>
          </h1>
          <p className="mt-5 text-[15px] sm:text-[16px] text-[#D4D4D8] leading-relaxed max-w-xl">
            SpotBounty is a marketplace connecting your business with independent vending
            operators. Tell us about your location — no fees, no equipment purchase, no obligation.
          </p>
          <a
            href="#biz-form"
            data-testid="hero-cta-scroll"
            className="sb-btn-primary mt-8 inline-flex items-center gap-2"
          >
            Request Vending Service
          </a>
        </div>
      </div>
    </section>
  );
}

// ─── Section: Trust strip (qualitative only — no fabricated metrics) ──
function TrustStrip() {
  const items = [
    { icon: Handshake,     label: "Independent vetted operators" },
    { icon: ShieldCheck,   label: "No fees or equipment purchase for your business" },
    { icon: Building2,     label: "Single-site or multi-site coordination" },
    { icon: ClipboardList, label: "Procurement-aware matching" },
  ];
  return (
    <section className="border-b border-[#2A2F34] bg-[#0B0E11]">
      <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map(({ icon: Icon, label }, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FFC107]/10 border border-[#FFC107]/30 flex items-center justify-center text-[#FFC107] shrink-0">
              <Icon size={18} />
            </div>
            <div className="text-[13px] font-bold leading-snug pt-1.5">{label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Section: How it works (3 short steps) ────────────────────────────
function HowItWorks() {
  const steps = [
    { n: 1, t: "Tell us about your location", b: "Share the essentials so operators can evaluate the fit — location type, people count, hours, and what you're looking for." },
    { n: 2, t: "SpotBounty reviews and matches", b: "We review your location and, if it looks like a fit, work on connecting you with an independent vending operator serving your area." },
    { n: 3, t: "The operator takes it from there", b: "The operator contacts you directly to walk through machines, service, and any agreement — no equipment purchase for your business." },
  ];
  return (
    <section className="border-b border-[#2A2F34]">
      <div className="max-w-7xl mx-auto px-6 py-14 sm:py-16">
        <div className="sb-eyebrow">How it works</div>
        <h2 className="mt-3 text-[26px] sm:text-[32px] font-black leading-tight max-w-2xl">
          From request to operator introduction.
        </h2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.n} className="sb-card p-6" data-testid={`how-step-${s.n}`}>
              <div className="w-9 h-9 rounded-lg bg-[#FFC107] text-[#080B0D] flex items-center justify-center font-black">{s.n}</div>
              <div className="mt-4 text-[15px] font-bold leading-snug">{s.t}</div>
              <div className="mt-2 text-[13px] text-[#A1A1AA] leading-relaxed">{s.b}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section: Request Form (progressive disclosure) ───────────────────
const INITIAL_FORM = {
  // Group 1: Location
  business: "", address: "", city: "", state: "", zip: "", type: "",
  sites_scope: "single", sites_count: "",
  // Group 2: Workplace
  approx_people_bucket: "", operating_schedule: "", shifts: "",
  existing_vending_status: "", decision_maker_role: "",
  dm_referral_name: "", dm_referral_email: "",
  // Group 3: What you want
  desired: [], machines_desired: "",
  commission_expectation: "", commission_notes: "", notes: "",
  // Group 4: Contact
  contact: "", title: "", email: "", phone: "",
  // Meta
  website_hp: "",
};

function RequestForm({ onSuccess }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState("");
  const [tsToken, setTsToken] = useState("");
  const [consent, setConsent] = useState(false);
  const tsRef = useRef(null);
  const tsRequired = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const set = (k) => (e) => {
    const type = e?.target?.type;
    const v = type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const setPhone = (e) => setForm((f) => ({ ...f, phone: formatPhoneUS(e.target.value) }));
  const toggleDesired = (v) =>
    setForm((f) => ({ ...f, desired: f.desired.includes(v) ? f.desired.filter((x) => x !== v) : [...f.desired, v] }));

  const requiredMissing = useMemo(() => {
    const missing = [];
    if (!form.business.trim())            missing.push("Business name");
    if (!form.address.trim())             missing.push("Address");
    if (!form.city.trim())                missing.push("City");
    if (!form.state)                      missing.push("State");
    if (!form.zip.trim())                 missing.push("ZIP");
    if (!form.type)                       missing.push("Location type");
    if (!form.sites_scope)                missing.push("Single/multiple sites");
    if (!form.approx_people_bucket)       missing.push("Approximate people");
    if (form.desired.length === 0)        missing.push("At least one vending type (or 'Not sure')");
    if (!form.decision_maker_role)        missing.push("Decision-maker role");
    if (!form.contact.trim())             missing.push("Contact name");
    if (!form.email.trim())               missing.push("Email");
    return missing;
  }, [form]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (requiredMissing.length > 0) {
      setBanner(`Please complete: ${requiredMissing.slice(0, 3).join(", ")}${requiredMissing.length > 3 ? "…" : ""}`);
      return;
    }
    if (!consent) {
      setBanner("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    if (tsRequired && !tsToken) {
      setBanner("Please complete the verification challenge and try again.");
      return;
    }
    setLoading(true); setBanner("");
    try {
      const r = await fetch(`${API}/api/preregister/business`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          business_name:          form.business.trim(),
          contact_name:           form.contact.trim(),
          title:                  form.title.trim() || null,
          email:                  form.email.trim(),
          phone:                  form.phone.replace(/\D/g, "") || null,
          address_line1:          form.address.trim(),
          city:                   form.city.trim(),
          state:                  form.state.toUpperCase(),
          zip:                    form.zip.trim(),
          business_type:          form.type,
          approx_people:          null, // legacy int column — bucketed value below
          approx_people_bucket:   form.approx_people_bucket,
          hours:                  null,
          operating_schedule:     form.operating_schedule || null,
          num_shifts:             form.shifts ? parseInt(form.shifts, 10) : null,
          current_vending:        form.existing_vending_status === "yes" ? true :
                                  form.existing_vending_status === "no"  ? false : null,
          existing_vending_status:form.existing_vending_status || null,
          desired_vending_types:  form.desired,
          machines_desired:       form.machines_desired || null,
          decision_maker_role:    form.decision_maker_role,
          dm_referral_can:        form.decision_maker_role === "can_connect" ? true : null,
          dm_referral_name:       form.decision_maker_role === "can_connect" ? (form.dm_referral_name.trim() || null) : null,
          dm_referral_email:      form.decision_maker_role === "can_connect" ? (form.dm_referral_email.trim() || null) : null,
          commission_expectation: form.commission_expectation || null,
          commission_notes:       form.commission_expectation === "specific" ? (form.commission_notes.trim() || null) : null,
          sites_scope:            form.sites_scope,
          sites_count:            form.sites_scope === "multi" && form.sites_count ? parseInt(form.sites_count, 10) : null,
          notes:                  form.notes.trim() || null,
          consent_ack:            true,
          consent_version:        CONSENT_VERSION,
          referral_code:          readCookie("sb_ref") || null,
          website_hp:             form.website_hp,
          turnstile_token:        tsToken,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 200 && data.status === "created") {
        toast.success("Request submitted.");
        onSuccess({
          contact_name:       form.contact.trim(),
          email:              form.email.trim(),
          business_name:      form.business.trim(),
          reference_number:   data.reference_number || "",
          possible_duplicate: !!data.possible_duplicate,
        });
        return;
      }
      if (r.status === 422 && data.status === "turnstile_failed") {
        setBanner("Verification failed. Please try again.");
      } else if (r.status === 422) {
        const errList = Array.isArray(data.errors) ? data.errors.map((x) => x.field).join(", ") : "";
        setBanner(errList ? `Please review: ${errList}.` : "Please review the form and try again.");
      } else if (r.status === 429) {
        setBanner("Too many attempts. Please try again later.");
      } else {
        setBanner("Something went wrong. Please try again.");
      }
      setTsToken(""); tsRef.current?.reset();
    } catch {
      setBanner("Something went wrong. Please try again.");
      setTsToken(""); tsRef.current?.reset();
    } finally {
      setLoading(false);
    }
  }

  return (
    <section id="biz-form" className="border-b border-[#2A2F34]">
      <div className="max-w-4xl mx-auto px-6 py-14 sm:py-16">
        <div className="sb-eyebrow">The request</div>
        <h2 className="mt-3 text-[26px] sm:text-[32px] font-black leading-tight">Tell us about your location.</h2>
        <p className="mt-3 text-[13.5px] text-[#A1A1AA] max-w-2xl leading-relaxed">
          Fields marked with a gold asterisk <span className="text-[#FFC107]">*</span> are required.
          Everything else is optional but helps us match you faster.
        </p>

        <form data-testid="business-form" onSubmit={handleSubmit} className="mt-8 space-y-5">
          {banner && (
            <div className="sb-card p-3 border-[#EF4444]/40 flex items-start gap-2" style={{ background: "rgba(239,68,68,.06)" }} data-testid="biz-banner">
              <AlertCircle size={14} className="text-[#EF4444] mt-0.5 shrink-0" />
              <span className="text-[12.5px] text-[#EF4444]">{banner}</span>
            </div>
          )}

          {/* GROUP 1 — LOCATION */}
          <FormGroup title="1 · About your location">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Business / organization name" required>
                <input required data-testid="biz-name" className="sb-input" value={form.business} onChange={set("business")} />
              </Field>
              <Field label="Location type" required>
                <select required data-testid="biz-type" className="sb-input" value={form.type} onChange={set("type")}>
                  <option value="">Select…</option>
                  {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Street address" required className="md:col-span-2">
                <input required data-testid="biz-address" className="sb-input" value={form.address} onChange={set("address")} />
              </Field>
              <Field label="City" required>
                <input required data-testid="biz-city" className="sb-input" value={form.city} onChange={set("city")} />
              </Field>
              <Field label="State" required>
                <select required data-testid="biz-state" className="sb-input" value={form.state} onChange={set("state")}>
                  <option value="">Select…</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="ZIP" required>
                <input required data-testid="biz-zip" className="sb-input" value={form.zip} onChange={set("zip")} inputMode="numeric" maxLength={10} />
              </Field>
              <Field label="Locations" required>
                <div className="flex gap-2">
                  {[["single", "Single"], ["multi", "Multiple"]].map(([k, l]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, sites_scope: k }))}
                      className={`sb-pill ${form.sites_scope === k ? "sb-pill-gold" : "sb-pill-gray"}`}
                      data-testid={`biz-sites-${k}`}
                    >{l}</button>
                  ))}
                </div>
              </Field>
              {form.sites_scope === "multi" && (
                <Field label="Approximate number of locations (optional)">
                  <input
                    type="number" min="1" max="10000"
                    data-testid="biz-sites-count"
                    className="sb-input"
                    value={form.sites_count}
                    onChange={set("sites_count")}
                    placeholder="e.g. 12"
                  />
                </Field>
              )}
            </div>
          </FormGroup>

          {/* GROUP 2 — WORKPLACE */}
          <FormGroup title="2 · Your workplace">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Approximate people (employees / residents / visitors)" required className="md:col-span-2">
                <select required data-testid="biz-people-bucket" className="sb-input" value={form.approx_people_bucket} onChange={set("approx_people_bucket")}>
                  <option value="">Select…</option>
                  {PEOPLE_BUCKETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
              </Field>
              <Field label="Operating schedule (optional)">
                <select data-testid="biz-schedule" className="sb-input" value={form.operating_schedule} onChange={set("operating_schedule")}>
                  <option value="">Select…</option>
                  {SCHEDULES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
              {form.operating_schedule === "multi_shift" && (
                <Field label="Number of shifts (optional)">
                  <div className="flex gap-2">
                    {["1", "2", "3"].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, shifts: n }))}
                        className={`sb-pill ${form.shifts === n ? "sb-pill-gold" : "sb-pill-gray"}`}
                        data-testid={`biz-shifts-${n}`}
                      >{n} shift{n !== "1" && "s"}</button>
                    ))}
                  </div>
                </Field>
              )}
              <Field label="Existing vending on site (optional)" className="md:col-span-2">
                <div className="flex flex-wrap gap-2">
                  {[["yes", "Yes"], ["no", "No"], ["not_sure", "Not sure"]].map(([k, l]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, existing_vending_status: k }))}
                      className={`sb-pill ${form.existing_vending_status === k ? "sb-pill-gold" : "sb-pill-gray"}`}
                      data-testid={`biz-existing-${k}`}
                    >{l}</button>
                  ))}
                </div>
              </Field>
              <Field label="Your role in the decision" required className="md:col-span-2">
                <div className="grid gap-2">
                  {DM_ROLES.map((r) => (
                    <label key={r.value} className={`sb-card p-3 flex items-start gap-3 cursor-pointer text-[13px] ${form.decision_maker_role === r.value ? "border-[#FFC107]/60 bg-[#FFC107]/[.04]" : ""}`}>
                      <input
                        type="radio" name="dm_role"
                        checked={form.decision_maker_role === r.value}
                        onChange={() => setForm((f) => ({ ...f, decision_maker_role: r.value }))}
                        data-testid={`biz-dm-${r.value}`}
                        className="mt-[3px] w-4 h-4 accent-[#FFC107] shrink-0"
                      />
                      <span className="text-white/90 leading-relaxed">{r.label}</span>
                    </label>
                  ))}
                </div>
              </Field>
              {form.decision_maker_role === "can_connect" && (
                <>
                  <Field label="Decision-maker name (optional)">
                    <input data-testid="biz-dm-name" className="sb-input" value={form.dm_referral_name} onChange={set("dm_referral_name")} />
                  </Field>
                  <Field label="Decision-maker email (optional)">
                    <input type="email" data-testid="biz-dm-email" className="sb-input" value={form.dm_referral_email} onChange={set("dm_referral_email")} />
                  </Field>
                </>
              )}
            </div>
          </FormGroup>

          {/* GROUP 3 — WHAT YOU WANT */}
          <FormGroup title="3 · What you're looking for">
            <div className="grid gap-4">
              <Field label="Desired vending types (select all that apply)" required>
                <div className="flex flex-wrap gap-2" data-testid="biz-vending-types">
                  {VENDING_TYPES.map((v) => (
                    <button
                      key={v.value}
                      type="button"
                      onClick={() => toggleDesired(v.value)}
                      className={`sb-pill ${form.desired.includes(v.value) ? "sb-pill-gold" : "sb-pill-gray"}`}
                      data-testid={`biz-vending-${v.value.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    >{v.label}</button>
                  ))}
                </div>
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Approximate number of machines (optional)">
                  <div className="flex flex-wrap gap-2">
                    {MACHINES_DESIRED.map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, machines_desired: m.value }))}
                        className={`sb-pill ${form.machines_desired === m.value ? "sb-pill-gold" : "sb-pill-gray"}`}
                        data-testid={`biz-machines-${m.value}`}
                      >{m.label}</button>
                    ))}
                  </div>
                </Field>
                <Field label="Commission / revenue-share expectations (optional)">
                  <select data-testid="biz-commission" className="sb-input" value={form.commission_expectation} onChange={set("commission_expectation")}>
                    <option value="">Select…</option>
                    {COMMISSION_EXPECTATIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
              </div>
              {form.commission_expectation === "specific" && (
                <Field label="Commission specifics (optional)">
                  <textarea data-testid="biz-commission-notes" className="sb-input" rows={3} value={form.commission_notes} onChange={set("commission_notes")}
                    placeholder="e.g. 15% revenue share on gross sales, monthly reporting…" />
                </Field>
              )}
              <Field label="Notes / special requirements (optional)">
                <textarea data-testid="biz-notes" className="sb-input" rows={4} value={form.notes} onChange={set("notes")}
                  placeholder="Loading dock hours, product preferences, procurement requirements, insurance limits, reporting needs…" />
              </Field>
            </div>
          </FormGroup>

          {/* GROUP 4 — CONTACT & VERIFY */}
          <FormGroup title="4 · Contact & verify">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Contact name" required>
                <input required data-testid="biz-contact" className="sb-input" value={form.contact} onChange={set("contact")} autoComplete="name" />
              </Field>
              <Field label="Title (optional)">
                <input data-testid="biz-title" className="sb-input" value={form.title} onChange={set("title")} autoComplete="organization-title" />
              </Field>
              <Field label="Email" required>
                <input required type="email" data-testid="biz-email" className="sb-input" value={form.email} onChange={set("email")} autoComplete="email" />
              </Field>
              <Field label="Phone (optional)">
                <input
                  type="tel"
                  data-testid="biz-phone"
                  className="sb-input"
                  value={form.phone}
                  onChange={setPhone}
                  placeholder="(555) 123-4567"
                  autoComplete="tel"
                />
              </Field>
            </div>

            <div className="sb-card p-4 flex items-start gap-3 border-[#FFC107]/25 mt-4" style={{ background: "rgba(255,193,7,.04)" }}>
              <ShieldCheck size={16} className="text-[#FFC107] mt-0.5 shrink-0" />
              <p className="text-[12.5px] text-[#A1A1AA] leading-relaxed">
                SpotBounty is a marketplace connecting businesses with independent vending
                operators. SpotBounty does not install or operate vending machines directly.
              </p>
            </div>

            <TurnstileWidget ref={tsRef} onToken={setTsToken} testId="biz-turnstile" />

            <label htmlFor="biz-consent" className="flex items-start gap-2 text-[12.5px] text-[#A1A1AA] leading-relaxed cursor-pointer mt-4">
              <input
                id="biz-consent" type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="biz-consent"
                className="mt-[3px] w-4 h-4 accent-[#FFC107] shrink-0"
                required
              />
              <span>
                I agree to the <Link to="/terms" className="text-[#FFC107] underline">Terms of Service</Link> and
                acknowledge the <Link to="/privacy" className="text-[#FFC107] underline">Privacy Policy</Link>,
                and confirm I&apos;m authorized to submit this request on behalf of the business.
              </span>
            </label>

            <button
              type="submit"
              disabled={loading || !consent || (tsRequired && !tsToken)}
              data-testid="biz-submit"
              className="sb-btn-primary w-full mt-4 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Request Vending Service"}
            </button>

            <input
              type="text" name="website_hp"
              value={form.website_hp}
              onChange={set("website_hp")}
              tabIndex={-1}
              autoComplete="off"
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
            />
          </FormGroup>
        </form>
      </div>
    </section>
  );
}

// ─── Section: Confirmation State ──────────────────────────────────────
function ConfirmationState({ data }) {
  const d = data || {};
  const ref = d.reference_number || "";
  return (
    <section className="border-b border-[#2A2F34]">
      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
        <div className="sb-card p-8 sm:p-12" data-testid="biz-confirmation">
          <CheckCircle2 size={56} className="text-[#FFC107]" strokeWidth={1.5} />
          <div className="mt-6 sb-eyebrow">Request received</div>
          <h1 className="mt-3 text-[32px] sm:text-[40px] font-black leading-tight">
            YOUR VENDING REQUEST IS IN.
          </h1>
          <p className="mt-4 text-[15px] text-[#D4D4D8]">
            Thanks, <span className="text-white font-bold">{d.contact_name || "there"}</span>.
            {ref && <> Your reference number is <span className="text-[#FFC107] font-bold" data-testid="biz-confirmation-ref">{ref}</span>.</>}
          </p>

          <div className="mt-8">
            <div className="sb-eyebrow">What happens next</div>
            <ol className="mt-4 grid gap-4">
              {[
                "SpotBounty reviews the location you submitted.",
                "We may contact you if we need additional information.",
                "If the location looks like a fit, we work on connecting you with an independent vending operator.",
                "The operator handles machine and service details with you directly.",
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-[#FFC107]/10 border border-[#FFC107]/40 text-[#FFC107] font-black flex items-center justify-center shrink-0">
                    {i + 1}
                  </div>
                  <div className="text-[14px] text-[#D4D4D8] leading-relaxed pt-1">{s}</div>
                </li>
              ))}
            </ol>
          </div>

          {d.email && (
            <p className="mt-8 text-[13px] text-[#A1A1AA]">
              You&apos;ll also receive an email confirmation shortly at <span className="text-white/90">{d.email}</span>.
            </p>
          )}

          {d.possible_duplicate && (
            <div className="mt-6 sb-card p-3 border-[#F59E0B]/40 text-[12.5px] text-[#F59E0B]" style={{ background: "rgba(245,158,11,.06)" }}>
              We noticed a similar prior request for this address — we&apos;ll consolidate on our end.
            </div>
          )}

          <div className="mt-10 flex gap-3">
            <Link to="/" className="sb-btn-secondary" data-testid="biz-confirmation-home">Back to home</Link>
            <a href="/#how-it-works" className="text-[13px] text-[#A1A1AA] hover:text-white flex items-center px-2">How it works</a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section: Procurement (truthful, marketplace-framed) ──────────────
function ProcurementSection() {
  return (
    <section className="border-b border-[#2A2F34] bg-[#0B0E11]">
      <div className="max-w-5xl mx-auto px-6 py-14 sm:py-16">
        <div className="sb-eyebrow">Business-Ready</div>
        <h2 className="mt-3 text-[26px] sm:text-[32px] font-black leading-tight max-w-3xl">
          Have procurement requirements? Tell us what your organization needs.
        </h2>
        <p className="mt-5 text-[14px] text-[#D4D4D8] leading-relaxed max-w-3xl">
          When matching a location, SpotBounty can take requirements such as insurance / COI
          availability, W-9 documentation, vendor agreements, service expectations, and reporting
          needs into account. Documentation, contracting, and ongoing vending service are provided
          directly by the selected independent vending operator.
        </p>
        <p className="mt-4 text-[13px] text-[#A1A1AA] max-w-3xl">
          Multi-site portfolios are welcome — share the site count on the form and outline
          coordination or reporting requirements in the notes.
        </p>
      </div>
    </section>
  );
}

// ─── Section: FAQ (accordion) ──────────────────────────────────────────
function FaqAccordion() {
  const [open, setOpen] = useState(0);
  return (
    <section className="border-b border-[#2A2F34]">
      <div className="max-w-4xl mx-auto px-6 py-14 sm:py-16">
        <div className="sb-eyebrow">Common questions</div>
        <h2 className="mt-3 text-[26px] sm:text-[32px] font-black leading-tight">Answers before you submit.</h2>
        <div className="mt-8 divide-y divide-[#2A2F34]">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="py-3">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-start gap-4 text-left py-2 group"
                  aria-expanded={isOpen}
                >
                  <div className="w-8 h-8 rounded-lg bg-[#15191D] border border-[#2A2F34] flex items-center justify-center shrink-0 text-[#FFC107] group-hover:border-[#FFC107]/40 transition-colors">
                    {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                  </div>
                  <div className="flex-1 text-[15px] font-bold leading-snug pt-1">{item.q}</div>
                </button>
                {isOpen && (
                  <div className="pl-12 pr-2 pb-3 text-[13.5px] text-[#A1A1AA] leading-relaxed" data-testid={`faq-answer-${i}`}>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── UI helpers ───────────────────────────────────────────────────────
function FormGroup({ title, children }) {
  return (
    <div className="sb-card p-5 md:p-6">
      <div className="text-[11px] uppercase tracking-widest text-[#FFC107] font-bold">{title}</div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function Field({ label, required, className = "", children }) {
  return (
    <div className={className}>
      <label className="sb-label">
        {label} {required && <span className="text-[#FFC107]">*</span>}
      </label>
      {children}
    </div>
  );
}
